'use client'

import { Asset, AssetSummary } from '@/lib/assets/types'
import { useQuery } from '@tanstack/react-query'
import { getMarketplaceAssetTeamState } from '@/lib/marketplace/api'
import { MarketplaceAssetPurchaseContextItem, MarketplaceAssetTeamState } from '@/lib/marketplace/types'
import { isFreeAsset } from '@/lib/marketplace/asset-action-visibility'
import { useHeaderFeatures } from '@/lib/contexts/HeaderFeaturesContext'

type MarketplaceAssetLike = Pick<AssetSummary | Asset, 'id' | 'uuid' | 'level' | 'marketplace'>

interface UseMarketplacePurchaseContextOptions {
  enabled?: boolean
}

const EMPTY_TEAM_STATE: MarketplaceAssetTeamState = {
  owned_asset_uuids: [],
  in_cart_asset_uuids: [],
}

const getIndexedMarketplaceAssets = (assets: MarketplaceAssetLike[]) => {
  return assets.filter(
    (
      asset
    ): asset is MarketplaceAssetLike & {
      uuid: string
      marketplace: NonNullable<MarketplaceAssetLike['marketplace']>
    } =>
      typeof asset.uuid === 'string' &&
      asset.uuid.length > 0 &&
      asset.marketplace !== null &&
      asset.marketplace !== undefined
  )
}

const parseMarketplaceAssetId = (asset: MarketplaceAssetLike): number | null => {
  if (typeof asset.id === 'number' && Number.isFinite(asset.id)) {
    return asset.id
  }

  if (typeof asset.id === 'string' && asset.id.length > 0) {
    const parsed = Number.parseInt(asset.id, 10)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

export const useMarketplacePurchaseContext = (
  assets: MarketplaceAssetLike[],
  { enabled }: UseMarketplacePurchaseContextOptions = {}
) => {
  const { marketplaceCartEnabled } = useHeaderFeatures()
  const isEnabled = enabled ?? (marketplaceCartEnabled === true)
  const indexedMarketplaceAssets = getIndexedMarketplaceAssets(assets)
  const assetUuids = [...new Set(indexedMarketplaceAssets.map((asset) => asset.uuid))]
  const skippedAssets = assets.length - indexedMarketplaceAssets.length

  if (isEnabled && process.env.NODE_ENV !== 'production' && skippedAssets > 0) {
    console.debug('[marketplace] skipped team-state lookup for assets missing marketplace listing data', {
      totalAssets: assets.length,
      indexedMarketplaceAssets: assetUuids.length,
      skippedAssets,
    })
  }

  const teamStateQuery = useQuery({
    queryKey: ['marketplace', 'team-state', assetUuids],
    queryFn: async ({ signal }) => {
      const data = await getMarketplaceAssetTeamState(assetUuids, { signal })

      if (process.env.NODE_ENV !== 'production') {
        console.debug('[marketplace] team-state resolved', {
          requested_asset_uuids: assetUuids,
          owned_asset_uuids: data.owned_asset_uuids,
          in_cart_asset_uuids: data.in_cart_asset_uuids,
        })
      }

      return data
    },
    enabled: isEnabled && assetUuids.length > 0,
    placeholderData: EMPTY_TEAM_STATE,
    staleTime: 30 * 1000,
  })

  const ownedAssetUuids = new Set(teamStateQuery.data?.owned_asset_uuids ?? EMPTY_TEAM_STATE.owned_asset_uuids)
  const inCartAssetUuids = new Set(teamStateQuery.data?.in_cart_asset_uuids ?? EMPTY_TEAM_STATE.in_cart_asset_uuids)

  return {
    ...teamStateQuery,
    data: isEnabled
      ? indexedMarketplaceAssets.reduce<Record<string, MarketplaceAssetPurchaseContextItem>>((acc, asset) => {
          const owned = ownedAssetUuids.has(asset.uuid)
          const inCart = inCartAssetUuids.has(asset.uuid)
          const purchasable = !isFreeAsset(asset) && asset.marketplace.purchasable

          acc[asset.uuid] = {
            asset_id: parseMarketplaceAssetId(asset),
            asset_uuid: asset.uuid,
            owned,
            purchasable,
            default_price: asset.marketplace.default_price,
            can_add_to_cart: purchasable && !owned && !inCart,
          }

          return acc
        }, {})
      : {},
  }
}
